import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Spin } from "antd";
import { ArrowLeftOutlined, DownloadOutlined, DownOutlined } from "@ant-design/icons";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import api, { extractFireDACData } from "../services/api";
import { gerarExtratoSimplificado } from "../utils/extratoSimplificadoPdf";

// ── Hook de responsividade (mesmo padrão usado no Dashboard) ────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", handler);
    handler();
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ─── HELPERS ──────────────────────────────────────────────────────────────────

/**
 * Conversão numérica defensiva.
 * O Postgres devolve colunas `money` que, dependendo da serialização do FireDAC,
 * podem chegar como "R$ 15.240,00" em vez de 15240. Number() nesse formato
 * retorna NaN e contamina silenciosamente todos os cálculos abaixo.
 * Aqui tratamos ambos os formatos e devolvemos 0 para qualquer coisa inválida.
 */
const toNum = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  let s = String(v).replace(/[R$\s]/g, "");
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const fmtBRL = (v) => `R$ ${toNum(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`;
const fmtMil = (v) => toNum(v).toLocaleString("pt-BR");
const fmtPct = (v) => `${toNum(v).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}%`;
const fmtNum = (v) => toNum(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (v) => {
  if (!v) return "—";
  const s = String(v).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  const compT = s.match(/^(\d{4})(\d{2})(\d{2})T/);
  if (compT) return `${compT[3]}/${compT[2]}/${compT[1]}`;
  if (s.match(/^\d{2}\/\d{2}\/\d{4}/)) return s.slice(0, 10);
  return s;
};

// Normaliza chaves para minúsculo (FireDAC pode retornar maiúsculo)
const norm = (row) => {
  if (!row || typeof row !== "object") return {};
  const out = {};
  for (const [k, v] of Object.entries(row)) out[k.toLowerCase()] = v;
  return out;
};

// ─── REGRA DE NEGÓCIO: ECONOMIA ───────────────────────────────────────────────
/**
 * A economia só existe quando houve LASTRO EM MILHAS.
 *
 * Ordem de precedência:
 *  1. Se o backend informou economia de fato (> 0), ela manda — é o caso da
 *     hospedagem, que já chega calculada em hos_veconomia.
 *  2. Sem milhas OU sem custo médio (ex.: CONSOLIDADORA, venda paga 100% em
 *     cash/voucher) não há economia a apurar => 0. NUNCA reconstruir a partir
 *     do valor pagante, sob pena de exibir "100% de economia" numa compra à vista.
 *  3. Caso normal: valor pagante − custo das milhas − extras.
 */
const calcularEconomia = (row) => {
  const econBanco = toNum(row.ven_veconomia ?? row.ven_economia ?? row.veconomia);
  if (econBanco > 0) return econBanco;

  const milhas = toNum(row.ven_qtde_milhas ?? row.qtde_milhas);
  const custo = toNum(row.tra_vunitario ?? row.vunitario);
  if (milhas <= 0 || custo <= 0) return 0;

  const total = toNum(row.tra_vtotal);
  const taxas = toNum(row.ven_taxas ?? row.taxas);
  return Math.max(0, total - ((milhas * custo) / 1000) - taxas);
};

const calcularPercentual = (row) => {
  const pctBanco = toNum(row.ven_peconomia ?? row.peconomia);
  if (pctBanco > 0) return pctBanco;

  const total = toNum(row.tra_vtotal);
  const econ = calcularEconomia(row);
  return total > 0 && econ > 0 ? (econ / total) * 100 : 0;
};

const DARK_NAV = "#1e3a4a";
const ORANGE = "#f5a623";

// ─── LOGOS ────────────────────────────────────────────────────────────────────
const PROGRAMA_LIST = [
  // ── Nacionais ──────────────────────────────────────────────────────
  { keys: ["livelo"],                    bg: "#e8008b", letra: "LV"  },
  { keys: ["itaú pontos","itau pontos"], bg: "#d48c0c", letra: "IT"  },
  { keys: ["smiles"],                    bg: "#ff6600", letra: "S"   },
  { keys: ["azul fidelidade","azul"],    bg: "#003087", letra: "A"   },
  { keys: ["coopera pj"],                bg: "#007b5e", letra: "CPJ" },
  { keys: ["coopera"],                   bg: "#007b5e", letra: "COO" },
  { keys: ["latam"],                     bg: "#d81a36", letra: "L"   },
  { keys: ["tap"],                       bg: "#009900", letra: "TAP" },
  { keys: ["max milhas"],                bg: "#1e3a6e", letra: "MM"  },
  { keys: ["hotmilhas"],                 bg: "#cc3300", letra: "HM"  },
  { keys: ["ipiranga"],                  bg: "#f5a800", letra: "IP"  },
  { keys: ["esfera empresas"],           bg: "#8b0000", letra: "EE"  },
  { keys: ["esfera"],                    bg: "#8b0000", letra: "E_"  },
  { keys: ["bank milhas"],               bg: "#0066cc", letra: "BM"  },
  { keys: ["cash milhas"],               bg: "#5a5a5a", letra: "CM"  },
  { keys: ["curtaí","curtai"],           bg: "#e8a000", letra: "CT"  },
  { keys: ["compro suas milhas"],        bg: "#1e3a4a", letra: "CSM" },
  { keys: ["átomos","atomos"],           bg: "#000000", letra: "ÁT"  },
  { keys: ["gpa"],                       bg: "#00aa44", letra: "GPA" },
  { keys: ["pontos caixa"],              bg: "#007b5e", letra: "PC"  },
  { keys: ["caixa empresas"],            bg: "#007b5e", letra: "CE"  },
  { keys: ["sicredi empresarial"],       bg: "#009900", letra: "SCE" },
  { keys: ["sicredi"],                   bg: "#009900", letra: "SC"  },
  { keys: ["dotz"],                      bg: "#ff6600", letra: "DZ"  },
  { keys: ["porto plus"],                bg: "#0057a8", letra: "PP"  },
  { keys: ["inter loop"],                bg: "#ff6b00", letra: "IL"  },
  { keys: ["inter empresas"],            bg: "#ff6b00", letra: "IE"  },
  { keys: ["volare"],                    bg: "#c8102e", letra: "VL"  },
  { keys: ["membership","amex"],         bg: "#006fcf", letra: "MR"  },
  { keys: ["safra"],                     bg: "#003366", letra: "SR"  },
  { keys: ["suma"],                      bg: "#1e3a4a", letra: "SU"  },
  { keys: ["cressol"],                   bg: "#007b5e", letra: "CR"  },
  { keys: ["banrisul"],                  bg: "#003087", letra: "BR"  },
  { keys: ["unicred"],                   bg: "#009900", letra: "UC"  },
  { keys: ["milhas plus"],               bg: "#e8a000", letra: "M+"  },
  { keys: ["xp pontos"],                 bg: "#1a1a2e", letra: "XP"  },
  { keys: ["nubank"],                    bg: "#820ad1", letra: "NU"  },
  { keys: ["ailos"],                     bg: "#007b5e", letra: "AI"  },
  { keys: ["banestes"],                  bg: "#003087", letra: "BN"  },
  { keys: ["bb empresas"],               bg: "#f5a800", letra: "BB"  },
  { keys: ["itaú empresas","itau empresas"], bg: "#d48c0c", letra: "ITE" },
  { keys: ["pontos btg"],                bg: "#1a1a2e", letra: "BTG" },

  // ── Internacionais ──────────────────────────────────────────────────
  { keys: ["iberia"],                    bg: "#cc0000", letra: "IB"  },
  { keys: ["mileageplus","united"],      bg: "#002244", letra: "UA"  },
  { keys: ["all accor","accor"],         bg: "#c8102e", letra: "AL"  },
  { keys: ["Qantas","qantas"],           bg: "#4e0000", letra: "QT"  },
  { keys: ["ihg"],                       bg: "#6a0dad", letra: "IHG" },
  { keys: ["aadvantage","american"],     bg: "#0078d2", letra: "AA"  },
  { keys: ["flying blue"],               bg: "#003087", letra: "FB"  },
  { keys: ["british"],                   bg: "#2b4090", letra: "BA"  },
  { keys: ["qatar"],                     bg: "#5c0632", letra: "QR"  },
  { keys: ["aeroplan"],                  bg: "#c8102e", letra: "AP"  },
  { keys: ["copa","connectmiles"],       bg: "#0033a0", letra: "CM"  },
  { keys: ["delta"],                     bg: "#c8102e", letra: "DL"  },
  { keys: ["emirates"],                  bg: "#c8102e", letra: "EK"  },
  { keys: ["virgin","flying club"],      bg: "#e8003d", letra: "VS"  },
  { keys: ["finnair"],                   bg: "#003580", letra: "AY"  },
  { keys: ["wyndham"],                   bg: "#003087", letra: "WY"  },
];

function getProgramaStyle(nome) {
  if (!nome) return { bg: DARK_NAV, letra: "★" };
  const key = nome.toLowerCase();
  for (const prog of PROGRAMA_LIST) {
    if (prog.keys.some(k => key.includes(k))) return { bg: prog.bg, letra: prog.letra };
  }
  const colors = ["#6B21A8","#0369A1","#065F46","#92400E","#1e3a4a","#9D174D"];
  return { bg: colors[nome.charCodeAt(0) % colors.length], letra: nome.substring(0,2).toUpperCase() };
}

function AirlineLogo({ nome, size = 34 }) {
  const s = getProgramaStyle(nome);
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: s.bg, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.32, fontWeight: 700, flexShrink: 0, boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>
      {s.letra}
    </div>
  );
}

// ─── ABA POSIÇÃO ──────────────────────────────────────────────────────────────
function TabPosicao({ cartoes, titulares, loading }) {
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    if (titulares.length > 0) {
      const init = {};
      titulares.forEach(t => { init[t.cli_id] = true; });
      setExpandidos(init);
    }
  }, [titulares]);

  const grupos = {};
  titulares.forEach(t => { grupos[t.cli_id] = { titular: t, cartoes: [] }; });
  cartoes.forEach(c => {
    const key = c.cli_id;
    if (grupos[key]) grupos[key].cartoes.push(c);
    else if (Object.keys(grupos).length > 0) grupos[Object.keys(grupos)[0]].cartoes.push(c);
  });

  const th = { fontSize: 12, fontWeight: 600, color: "#888", padding: "10px 16px", textAlign: "left", borderBottom: "1px solid #f0f0f0", background: "#fafafa", whiteSpace: "nowrap" };
  const td = (align = "left") => ({ padding: "11px 16px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#333", textAlign: align });

  return (
    <Spin spinning={loading}>
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
            <thead>
              <tr>{["Titular/Conta","Saldo","Custo Médio","Patrimônio","Próx Lotes a Expirar"].map((h,i)=>(
                <th key={i} style={{...th, width: i===0?"30%":"auto"}}>{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {Object.entries(grupos).map(([key, grupo]) => {
                const isOpen = expandidos[key] !== false;
                const nome = grupo.titular.cli_nome || "Titular";
                return (
                  <React.Fragment key={key}>
                    <tr onClick={() => setExpandidos(p=>({...p,[key]:!p[key]}))}
                      style={{ cursor:"pointer", background:"#f7f8fa" }}
                      onMouseEnter={e=>e.currentTarget.style.background="#eef2f7"}
                      onMouseLeave={e=>e.currentTarget.style.background="#f7f8fa"}>
                      <td colSpan={5} style={{ padding:"9px 16px", borderBottom:"1px solid #e8e8e8" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                          <span style={{ fontSize:10, color:"#aaa", transition:"transform 0.2s", display:"inline-block", transform: isOpen?"rotate(0deg)":"rotate(-90deg)" }}>▼</span>
                          <span style={{ fontSize:13, fontWeight:600, color:DARK_NAV }}>{nome}</span>
                        </div>
                      </td>
                    </tr>
                    {isOpen && grupo.cartoes.map((c,idx)=>{
                      const saldo = toNum(c.car_saldo_milhas);
                      const custo = toNum(c.car_vunitario);
                      const patrimonio = (saldo*custo)/1000;
                      const expira = c.car_expira===1||c.car_expira==="1";
                      const qtdeExp = toNum(c.car_qtde_expira);
                      return (
                        <tr key={c.car_id||idx} style={{ background: idx%2===0?"#fff":"#fafcff" }}
                          onMouseEnter={e=>e.currentTarget.style.background="#f0f7ff"}
                          onMouseLeave={e=>e.currentTarget.style.background=idx%2===0?"#fff":"#fafcff"}>
                          <td style={td()}>
                            <div style={{ display:"flex", alignItems:"center", gap:10, paddingLeft:24 }}>
                              <AirlineLogo nome={c.car_nome_programa} size={34} />
                              <span style={{ fontSize:13, fontWeight:500 }}>{c.car_nome_programa||"—"}</span>
                            </div>
                          </td>
                          <td style={td("right")}>{fmtMil(saldo)}</td>
                          <td style={td("right")}>{fmtBRL(custo)}</td>
                          <td style={td("right")}>{fmtBRL(patrimonio)}</td>
                          <td style={td()}>
                            {expira && qtdeExp > 0 && c.car_dtexpiracao ? (
                              <span style={{ fontSize:12, color:"#d46b08", background:"#fff7e6", border:"1px solid #ffd591", borderRadius:4, padding:"2px 8px", fontWeight:600, whiteSpace:"nowrap" }}>
                                {fmtMil(qtdeExp)} em {fmtDate(c.car_dtexpiracao)}
                              </span>
                            ) : <span style={{ color:"#bbb", fontSize:12 }}>—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
              {Object.keys(grupos).length===0 && !loading && (
                <tr><td colSpan={5} style={{ textAlign:"center", padding:"48px 0", color:"#bbb", fontSize:14 }}>Nenhuma conta encontrada</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Spin>
  );
}

// ─── NORMALIZAÇÃO DE HOSPEDAGEM ───────────────────────────────────────────────
function normalizaHospedagem(row) {
  const milhas     = toNum(row.hos_qtde_milhas);
  const custoTotal = toNum(row.hos_vcusto); // custo total das milhas usadas nesta hospedagem
  const custoUnit  = milhas > 0 ? custoTotal / (milhas / 1000) : 0; // <- ajustar se hos_vcusto já for por milheiro
  const dataCheckIn = row.hos_data_check_in || "";

  return {
    tra_categoria:   "HOSPEDAGEM",
    tra_data:        dataCheckIn,
    tra_vtotal:      toNum(row.hos_vreserva),
    tra_vunitario:   custoUnit,
    ven_origem:      row.hos_nome_hospedagem
                       ? `${row.hos_nome_hospedagem}${row.hos_voucher_reserva ? " • " + row.hos_voucher_reserva : ""}`
                       : "Hospedagem",
    ven_data_voo:    dataCheckIn,
    ven_qtde_milhas: milhas,
    ven_taxas:       0, // não existe campo de taxa/extra para hospedagem no backend atual
    ven_veconomia:   toNum(row.hos_veconomia),
    ven_peconomia:   toNum(row.hos_peconomia),
  };
}

// ─── ABA UTILIZAÇÃO ───────────────────────────────────────────────────────────
function TabUtilizacao({ clienteId }) {
  const isMobile = useIsMobile();
  const [relatorio, setRelatorio] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [totalEconomia, setTotalEconomia] = useState(0);

  useEffect(() => {
    if (!clienteId) return;
    setLoading(true);

    Promise.all([
      api.get("/ServerPrincipal/PesquisaUltilizacao", { params: { Cliente: clienteId } }),
      api.get("/ServerPrincipal/PesquisaHospedagem", { params: { Cliente: clienteId } })
        .catch((e) => { console.error("Falha ao buscar hospedagem:", e); return { data: [] }; }),
    ])
      .then(([resUtilizacao, resHospedagem]) => {
        const dataUtilizacao = extractFireDACData(resUtilizacao.data).map(norm);
        const dataHospedagem = extractFireDACData(resHospedagem.data).map(norm).map(normalizaHospedagem);

        const data = [...dataUtilizacao, ...dataHospedagem];
        setRelatorio(data);

        // Calcula a economia total de TODOS os registros
        const total = data.reduce((s, r) => s + calcularEconomia(r), 0);
        setTotalEconomia(total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [clienteId]);

  // Rótulo de utilização baseado na categoria
  const getCatLabel = (row) => {
    const cat = String(row.tra_categoria || row.ven_origem || "").toUpperCase();
    if (cat.includes("VENDA"))          return "Venda";
    if (cat.includes("PASSAGEM") || cat.includes("VOO") || cat.includes("VÔO")) return "Vôo - Milhas Próprias";
    if (cat.includes("HOSPEDAGEM") || cat.includes("HOTEL")) return "Hospedagem";
    if (cat.includes("TRANSFERENCIA") || cat.includes("TRANSFERÊNCIA")) return "Transferência";
    if (cat.includes("SAIDA") || cat.includes("SAÍDA")) return "Saída Manual";
    if (cat.includes("COMPRA"))         return "Compra";
    if (cat.includes("CADASTRO"))       return cat.replace(/_/g, " ");
    return row.tra_categoria || row.ven_origem || "—";
  };

  const thStyle = {
    fontSize: 11, fontWeight: 700, color: "#888",
    padding: "9px 12px", textAlign: "left",
    borderBottom: "2px solid #e8e8e8", background: "#fafafa",
    whiteSpace: "pre-line",
  };

  return (
    <Spin spinning={loading}>
      <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", overflow: "hidden" }}>

        {/* Header: apenas o total (sem seletor de ciclo) */}
        <div style={{ padding: isMobile ? "14px 16px" : "16px 20px", borderBottom: "1px solid #f0f0f0", display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "center", justifyContent: "space-between", gap: isMobile ? 12 : 0 }}>
          <div>
            <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600, marginBottom: 6 }}>Economia Total (Todos os Períodos):</div>
          </div>

          <div style={{ border: `2px solid #389e0d`, borderRadius: 8, padding: "12px 20px", textAlign: "center", minWidth: isMobile ? "auto" : 140 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#389e0d" }}>{fmtBRL(totalEconomia)}</div>
          </div>
        </div>

        {/* Tabela */}
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 960 }}>
            <thead>
              <tr>
                {[
                  "Utilização", "Identificação", "Data",
                  "Valor Pagante", "Milhas/Pontos\nUtilizados",
                  "Custo\nMédio", "Extras",
                  "Valor em\nMilhas", "Economia Gerada\n(R$)", "Economia Gerada\n(%)"
                ].map((h, i) => (
                  <th key={i} style={{ ...thStyle, textAlign: i >= 3 ? "right" : "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {relatorio.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} style={{ textAlign: "center", padding: "48px 0", color: "#bbb", fontSize: 14 }}>
                    Nenhum registro encontrado
                  </td>
                </tr>
              )}
              {relatorio.map((row, idx) => {
                const categoria  = row.tra_categoria || "";
                const origem     = row.ven_origem    || "";
                const dataVoo    = row.ven_data_voo  || row.tra_data || "";
                const vlrPagante = toNum(row.tra_vtotal);
                const milhas     = toNum(row.ven_qtde_milhas);
                const custoUnit  = toNum(row.tra_vunitario);
                const taxas      = toNum(row.ven_taxas);

                const economia = calcularEconomia(row);
                const pctEcon  = calcularPercentual(row);

                const vlrMilhas = (milhas > 0 && custoUnit > 0)
                  ? (milhas * custoUnit) / 1000
                  : 0;

                const temEcon = economia > 0;
                const semLastro = milhas <= 0 || custoUnit <= 0;

                return (
                  <tr key={idx}
                    style={{ background: idx % 2 === 0 ? "#fff" : "#fafcff" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#f0f7ff"}
                    onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafcff"}>

                    {/* Utilização */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#333" }}>
                      {getCatLabel(row)}
                    </td>

                    {/* Identificação */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 12, color: "#555", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                      title={origem || categoria || ""}>
                      {(origem || categoria || "—").substring(0, 35)}
                    </td>

                    {/* Data */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#666", whiteSpace: "nowrap" }}>
                      {fmtDate(dataVoo)}
                    </td>

                    {/* Valor Pagante */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#333", textAlign: "right" }}>
                      {vlrPagante > 0 ? fmtNum(vlrPagante) : "—"}
                    </td>

                    {/* Milhas/Pontos Utilizados */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, fontWeight: 600, color: "#333", textAlign: "right" }}>
                      {milhas > 0 ? fmtMil(milhas) : "—"}
                    </td>

                    {/* Custo Médio */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#555", textAlign: "right" }}>
                      {custoUnit > 0 ? fmtNum(custoUnit) : "—"}
                    </td>

                    {/* Extras (taxas) */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#555", textAlign: "right" }}>
                      {taxas > 0 ? fmtNum(taxas) : "0,00"}
                    </td>

                    {/* Valor em Milhas */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, color: "#333", textAlign: "right" }}>
                      {vlrMilhas > 0 ? fmtNum(vlrMilhas) : "—"}
                    </td>

                    {/* Economia Gerada R$ */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, fontWeight: 600, color: temEcon ? "#389e0d" : "#bbb", textAlign: "right" }}
                      title={!temEcon && semLastro ? "Economia não apurada para esta modalidade (sem uso de milhas)" : undefined}>
                      {temEcon ? fmtBRL(economia) : "—"}
                    </td>

                    {/* Economia Gerada % */}
                    <td style={{ padding: "11px 12px", borderBottom: "1px solid #f5f5f5", fontSize: 13, fontWeight: 600, color: pctEcon > 0 ? "#389e0d" : "#bbb", textAlign: "right" }}
                      title={pctEcon <= 0 && semLastro ? "Economia não apurada para esta modalidade (sem uso de milhas)" : undefined}>
                      {pctEcon > 0 ? fmtPct(pctEcon) : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>

            {/* Totais */}
            {relatorio.length > 0 && (
              <tfoot>
                <tr style={{ background: "#f8f9fa" }}>
                  <td colSpan={3} style={{ padding: "12px 12px", fontWeight: 700, fontSize: 13, color: "#333", borderTop: "2px solid #e8e8e8" }}>
                    TOTAL
                  </td>
                  <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 13, color: "#333", textAlign: "right", borderTop: "2px solid #e8e8e8" }}>
                    {fmtNum(relatorio.reduce((s, r) => s + toNum(r.tra_vtotal), 0))}
                  </td>
                  <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 13, color: "#333", textAlign: "right", borderTop: "2px solid #e8e8e8" }}>
                    {fmtMil(relatorio.reduce((s, r) => s + toNum(r.ven_qtde_milhas), 0))}
                  </td>
                  <td colSpan={2} style={{ borderTop: "2px solid #e8e8e8" }} />
                  <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 13, color: "#333", textAlign: "right", borderTop: "2px solid #e8e8e8" }}>
                    {fmtNum(relatorio.reduce((s, r) => s + toNum(r.ven_taxas), 0))}
                  </td>
                  <td style={{ padding: "12px 12px", fontWeight: 700, fontSize: 13, color: "#389e0d", textAlign: "right", borderTop: "2px solid #e8e8e8" }}>
                    {fmtBRL(totalEconomia)}
                  </td>
                  <td style={{ borderTop: "2px solid #e8e8e8" }} />
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Spin>
  );
}

// ─── ABA OBSERVAÇÕES ──────────────────────────────────────────────────────────
function TabObservacoes({ clienteId }) {
  const isMobile = useIsMobile();
  const [texto, setTexto]   = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  const handleSalvar = async () => {
    if (!texto.trim()) return;
    setSaving(true);
    try {
      await new Promise(r => setTimeout(r, 600));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ background: "#fff", borderRadius: 8, border: "1px solid #e8e8e8", padding: isMobile ? "18px 16px 16px" : "24px 24px 20px" }}>
      <p style={{ fontSize: 13, color: "#555", margin: "0 0 14px 0" }}>
        Registro dos comunicados para o cliente. Essas informações aparecerão em seu extrato.
      </p>
      <textarea value={texto} onChange={e => setTexto(e.target.value)} placeholder=""
        style={{ width: "100%", minHeight: 220, border: "1px solid #d9d9d9", borderRadius: 6, padding: "10px 12px", fontSize: 14, color: "#333", resize: "vertical", outline: "none", boxSizing: "border-box", lineHeight: 1.6, fontFamily: "'Segoe UI', sans-serif" }}
        onFocus={e => e.target.style.borderColor = "#4a90d9"}
        onBlur={e  => e.target.style.borderColor = "#d9d9d9"}
      />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
        <button onClick={handleSalvar} disabled={saving || !texto.trim()}
          style={{ padding: "8px 28px", background: "transparent", border: `1px solid ${saved ? "#52c41a" : ORANGE}`, borderRadius: 6, color: saved ? "#52c41a" : ORANGE, fontSize: 13, fontWeight: 700, cursor: saving || !texto.trim() ? "not-allowed" : "pointer", opacity: !texto.trim() ? 0.5 : 1, transition: "all 0.2s" }}
          onMouseEnter={e => { if (texto.trim() && !saving) { e.currentTarget.style.background = saved ? "#52c41a" : ORANGE; e.currentTarget.style.color = "#fff"; } }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = saved ? "#52c41a" : ORANGE; }}>
          {saving ? "Salvando..." : saved ? "✓ SALVO" : "SALVAR"}
        </button>
      </div>
    </div>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
export default function ExtratoAssinante() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { cliente, abaAtiva } = location.state || {};

  const [abaAtivaState, setAbaAtivaState] = useState(abaAtiva || "posicao");
  const [cartoes, setCartoes]             = useState([]);
  const [titulares, setTitulares]         = useState([]);
  const [loadingPos, setLoadingPos]       = useState(false);
  const [loadingPdf, setLoadingPdf]       = useState(false);
  const [loadingPdfSimples, setLoadingPdfSimples] = useState(false);

  const gestor = localStorage.getItem("userName") || "—";

  useEffect(() => { if (abaAtiva) setAbaAtivaState(abaAtiva); }, [abaAtiva]);

  useEffect(() => { if (cliente?.id) carregarPosicao(); }, [cliente]);

  const carregarPosicao = async () => {
    setLoadingPos(true);
    try {
      const titRes  = await api.get("/ServerPrincipal/PesquisaTitular", { params: { Cliente: cliente.id } });
      const titRows = extractFireDACData(titRes.data).map(norm);

      const seen = new Set();
      const titularesDeduplic = titRows.filter(t => {
        const k = String(t.cli_id ?? "");
        if (!k || k === "0" || seen.has(k)) return false;
        seen.add(k); return true;
      });

      const carResArr = await Promise.all(
        titularesDeduplic.map(t =>
          api.get("/ServerPrincipal/PesquisaCartoes", { params: { Cliente: t.cli_id } })
            .then(res => ({ rows: extractFireDACData(res.data).map(norm), tit: t }))
            .catch(() => ({ rows: [], tit: t }))
        )
      );

      const cartoesFlat = [];
      const seenCar = new Set();
      carResArr.forEach(({ rows, tit }) => {
        rows.forEach(c => {
          const k = String(c.car_id ?? "");
          if (!k || seenCar.has(k)) return;
          seenCar.add(k);
          cartoesFlat.push({
            car_id:            c.car_id,
            car_nome_programa: c.car_nome_programa ?? "",
            car_saldo_milhas:  toNum(c.car_saldo_milhas),
            car_vunitario:     toNum(c.car_vunitario),
            car_qtde_cpf:      toNum(c.car_qtde_cpf),
            car_qtde_expira:   toNum(c.car_qtde_expira),
            car_dtexpiracao:   c.car_dtexpiracao ?? null,
            car_expira:        c.car_expira      ?? 0,
            cli_id:            tit.cli_id,
          });
        });
      });

      setCartoes(cartoesFlat);
      setTitulares(titularesDeduplic.length > 0
        ? titularesDeduplic
        : [{ cli_id: cliente.id, cli_nome: cliente.nome }]);
    } catch (err) {
      console.error(err);
      setTitulares([{ cli_id: cliente.id, cli_nome: cliente.nome }]);
    } finally {
      setLoadingPos(false);
    }
  };

  const exportarPDF = async () => {
    setLoadingPdf(true);
    try {
      const relRes = await api.get("/ServerPrincipal/RelatorioEconomia", { params: { Cliente: cliente.id } });
      const linhas = extractFireDACData(relRes.data).map(norm);

      const doc   = new jsPDF({ orientation: "landscape" });
      const nome  = cliente?.nome  || "Cliente";
      const email = cliente?.email || "";
      const hoje  = new Date().toLocaleDateString("pt-BR");

      const fmtMp = (v) => new Intl.NumberFormat("pt-BR").format(Math.round(toNum(v)));
      const fmtCp = (v) => toNum(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const fmtPp = (v) => toNum(v).toFixed(2).replace(".", ",") + "%";

      doc.setFillColor(30, 58, 74);
      doc.rect(0, 0, 297, 28, "F");
      doc.setFontSize(16); doc.setTextColor(255,255,255); doc.setFont("helvetica","bold");
      doc.text("Extratos do Assinante por Titular", 14, 12);
      doc.setFontSize(9); doc.setFont("helvetica","normal");
      doc.text(`Gestor: ${gestor}   |   Cliente: ${nome}   |   Email: ${email}   |   Gerado em: ${hoje}`, 14, 22);

      let entMilhas = 0, saidaMilhas = 0, economia = 0;
      linhas.forEach(t => {
        entMilhas   += toNum(t.milhas_entrada);
        saidaMilhas += toNum(t.milhas_saida);
        economia    += toNum(t.economia_reais);
      });
      const saldoFinal = linhas.length > 0 ? toNum(linhas[linhas.length - 1].saldo_acumulado) : 0;

      const metricsY = 36;
      [
        { label: "Entradas",       valor: fmtMp(entMilhas)   + " milhas" },
        { label: "Saídas",         valor: fmtMp(saidaMilhas) + " milhas" },
        { label: "Economia Total", valor: "R$ " + fmtCp(economia) },
        { label: "Saldo Acum.",    valor: fmtMp(saldoFinal)  + " milhas" },
        { label: "Transações",     valor: String(linhas.length) },
      ].forEach((m, i) => {
        const x = 14 + i * 56;
        doc.setFillColor(245, 166, 35);
        doc.roundedRect(x, metricsY, 52, 16, 2, 2, "F");
        doc.setTextColor(255,255,255); doc.setFontSize(7); doc.setFont("helvetica","normal");
        doc.text(m.label, x + 26, metricsY + 5, { align: "center" });
        doc.setFontSize(9); doc.setFont("helvetica","bold");
        doc.text(m.valor, x + 26, metricsY + 12, { align: "center" });
      });

      doc.setTextColor(30,58,74); doc.setFontSize(11); doc.setFont("helvetica","bold");
      doc.text("Posição - Programas de Fidelidade", 14, metricsY + 26);

      const mapTitular = {};
      titulares.forEach(t => { mapTitular[t.cli_id] = t.cli_nome || nome; });

      autoTable(doc, {
        startY: metricsY + 30,
        head: [["Titular","Programa","Saldo","Custo Médio","Patrimônio","Próx. Expiração"]],
        body: cartoes.length > 0 ? cartoes.map(c => [
          mapTitular[c.cli_id] || nome,
          c.car_nome_programa || "—",
          fmtMp(c.car_saldo_milhas),
          "R$ " + fmtCp(c.car_vunitario),
          "R$ " + fmtCp((toNum(c.car_saldo_milhas) * toNum(c.car_vunitario)) / 1000),
          (c.car_expira === 1 || c.car_expira === "1") && toNum(c.car_qtde_expira) > 0
            ? fmtMp(c.car_qtde_expira) + " mi" : "—",
        ]) : [["Nenhum programa","","","","",""]],
        theme: "striped",
        headStyles: { fillColor: [30,58,74], textColor: 255, fontSize: 8, fontStyle: "bold" },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: { 0:{cellWidth:50}, 1:{cellWidth:60}, 2:{cellWidth:28,halign:"right"}, 3:{cellWidth:28,halign:"right"}, 4:{cellWidth:30,halign:"right"}, 5:{cellWidth:38,halign:"right"} },
      });

      const pageH = doc.internal.pageSize.height;
      let startUtilY = doc.lastAutoTable.finalY + 14;
      if (startUtilY > pageH - 50) { doc.addPage(); startUtilY = 20; }

      doc.setTextColor(30,58,74); doc.setFontSize(11); doc.setFont("helvetica","bold");
      doc.text("Utilização - Histórico de Transações", 14, startUtilY);

      autoTable(doc, {
        startY: startUtilY + 6,
        head: [["Data","Tipo","Categoria","Programa","Identificação","Entrada","Saída","Valor Pagante","C.Milheiro","Extras","Economia R$","% Econ.","Saldo"]],
        body: linhas.length > 0 ? linhas.map(t => {
          const dtFmt = t.data_formatada || fmtDate(t.tra_data);
          const valorPagante = toNum(t.valor_reais) - toNum(t.extras);
          return [
            dtFmt,
            t.tra_tipo || "—",
            t.tra_categoria || "—",
            t.car_nome_programa || "—",
            (t.identificacao || "—").slice(0, 35),
            toNum(t.milhas_entrada) > 0 ? fmtMp(t.milhas_entrada) : "—",
            toNum(t.milhas_saida)   > 0 ? fmtMp(t.milhas_saida)   : "—",
            valorPagante > 0 ? "R$ " + fmtCp(valorPagante) : "—",
            toNum(t.custo_unitario) > 0 ? fmtCp(t.custo_unitario) : "—",
            toNum(t.extras)         > 0 ? "R$ " + fmtCp(t.extras) : "0,00",
            toNum(t.economia_reais) > 0 ? "R$ " + fmtCp(t.economia_reais) : "—",
            toNum(t.percentual_economia) > 0 ? fmtPp(t.percentual_economia) : "—",
            fmtMp(t.saldo_acumulado),
          ];
        }) : [["Nenhuma transação","","","","","","","","","","","",""]],
        theme: "striped",
        headStyles: { fillColor: [30,58,74], textColor: 255, fontSize: 7, fontStyle: "bold" },
        styles: { fontSize: 7, cellPadding: 1.5 },
        columnStyles: { 0:{cellWidth:18}, 1:{cellWidth:12}, 2:{cellWidth:20}, 3:{cellWidth:26}, 4:{cellWidth:36}, 5:{cellWidth:16,halign:"right"}, 6:{cellWidth:16,halign:"right"}, 7:{cellWidth:18,halign:"right"}, 8:{cellWidth:16,halign:"right"}, 9:{cellWidth:14,halign:"right"}, 10:{cellWidth:18,halign:"right"}, 11:{cellWidth:12,halign:"right"}, 12:{cellWidth:18,halign:"right"} },
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8); doc.setTextColor(180,180,180);
        doc.text(`Página ${i} de ${pageCount}  |  AdmGVS — Gerado em ${hoje}`, 148, doc.internal.pageSize.height - 6, { align: "center" });
      }

      doc.save(`extrato_${nome.replace(/\s+/g, "_")}_${new Date().toISOString().slice(0,10)}.pdf`);
    } catch (err) {
      console.error("Erro ao gerar PDF:", err);
    } finally {
      setLoadingPdf(false);
    }
  };

  // ── Extrato simplificado (layout do PDF do dono do sistema) ────────────────
  const exportarPDFSimplificado = async () => {
    setLoadingPdfSimples(true);
    try {
      const [resU, resH] = await Promise.all([
        api.get("/ServerPrincipal/PesquisaUltilizacao", { params: { Cliente: cliente.id } }),
        api.get("/ServerPrincipal/PesquisaHospedagem", { params: { Cliente: cliente.id } })
          .catch(() => ({ data: [] })),
      ]);

      const rows = [
        ...extractFireDACData(resU.data).map(norm),
        ...extractFireDACData(resH.data).map(norm).map(normalizaHospedagem),
      ];

      const descricao = (row) => {
        const cat = String(row.tra_categoria || row.ven_origem || "").toUpperCase();
        const origem = row.ven_origem || "";
        let label = row.tra_categoria || "Utilização";
        if (cat.includes("HOSPEDAGEM") || cat.includes("HOTEL")) label = "Hospedagem - Milhas Próprias";
        else if (cat.includes("PASSAGEM") || cat.includes("VOO") || cat.includes("VÔO")) label = "Vôo - Milhas Próprias";
        else if (cat.includes("VENDA")) label = "Venda";
        return origem && !label.includes(origem) ? `${label} - ${origem}` : label;
      };

      const chave = (d) => String(d).split("/").reverse().join("");

      const utilizacao = rows
        .map((row) => {
          const milhas = toNum(row.ven_qtde_milhas);
          const cm = toNum(row.tra_vunitario);
          return {
            descricao: descricao(row),
            data: fmtDate(row.ven_data_voo || row.tra_data),
            lote: milhas,
            cm,
            pagante: toNum(row.tra_vtotal),
            extras: toNum(row.ven_taxas),
            preco: milhas > 0 && cm > 0 ? (milhas * cm) / 1000 : 0,
            economia: calcularEconomia(row),
            pct: calcularPercentual(row),
          };
        })
        .sort((a, b) => chave(b.data).localeCompare(chave(a.data)));

      await gerarExtratoSimplificado({
        cliente,
        gestor,
        gestorEmail: localStorage.getItem("userEmail") || "",
        gestorTelefone: localStorage.getItem("userPhone") || "",
        titulares,
        cartoes,
        utilizacao,
        comunicados: "", // ligar ao texto das Observações quando ele for persistido
        periodo: "",     // ex.: "20/02/2026 - 20/02/2027"
        logoUrl: "/images/logo.png",
      });
    } catch (err) {
      console.error("Erro ao gerar PDF simplificado:", err);
    } finally {
      setLoadingPdfSimples(false);
    }
  };

  const ABAS = [
    { key: "posicao",     label: "POSIÇÃO"     },
    { key: "utilizacao",  label: "UTILIZAÇÃO"  },
    { key: "observacoes", label: "OBSERVAÇÕES" },
  ];

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif" }}>

      {/* Header */}
      <div style={{ background: "#fff", borderBottom: "1px solid #e8e8e8", padding: isMobile ? "12px 14px" : "14px 24px", marginBottom: 20, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: isMobile ? "wrap" : "nowrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <button onClick={() => navigate("/dashboard", { state: { currentPage: "carteira" } })}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: "none", cursor: "pointer", fontSize: 13, color: "#888", padding: "4px 8px", borderRadius: 4, flexShrink: 0 }}
              onMouseEnter={e => e.currentTarget.style.color = DARK_NAV}
              onMouseLeave={e => e.currentTarget.style.color = "#888"}>
              <ArrowLeftOutlined />
            </button>
            <span style={{ fontSize: isMobile ? 13 : 15, fontWeight: 700, color: "#222", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {isMobile ? "📄 Extratos do Assinante" : "📄 Extratos do Assinante por Titular"}
            </span>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {/* Novo: extrato simplificado */}
            <button onClick={exportarPDFSimplificado} disabled={loadingPdfSimples}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${DARK_NAV}`, borderRadius: 6, padding: isMobile ? "6px 12px" : "8px 16px", cursor: loadingPdfSimples ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: DARK_NAV, opacity: loadingPdfSimples ? 0.7 : 1, transition: "all 0.2s", flexShrink: 0 }}
              onMouseEnter={e => { if (!loadingPdfSimples) { e.currentTarget.style.background = DARK_NAV; e.currentTarget.style.color = "#fff"; } }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = DARK_NAV; }}>
              <DownloadOutlined /> {loadingPdfSimples ? "Gerando..." : "EXTRATO SIMPLES"}
            </button>

            {/* Existente: extrato completo */}
            <button onClick={exportarPDF} disabled={loadingPdf}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "transparent", border: `1px solid ${ORANGE}`, borderRadius: 6, padding: isMobile ? "6px 12px" : "8px 16px", cursor: loadingPdf ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, color: ORANGE, opacity: loadingPdf ? 0.7 : 1, transition: "all 0.2s", flexShrink: 0 }}
              onMouseEnter={e => { if (!loadingPdf) { e.currentTarget.style.background = ORANGE; e.currentTarget.style.color = "#fff"; } }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = ORANGE; }}>
              <DownloadOutlined /> {loadingPdf ? "Gerando..." : "DOWNLOAD"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 8 : 40, marginTop: 14, flexWrap: "wrap" }}>
          {[
            { label: "Gestor",  valor: gestor },
            { label: "Cliente", valor: cliente?.nome  || "—" },
            { label: "Email",   valor: cliente?.email || "—" },
          ].map((item, i) => (
            <div key={i}>
              <div style={{ fontSize: 11, color: "#aaa", fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
              <div style={{ fontSize: i === 2 ? 13 : 14, fontWeight: i === 2 ? 400 : 700, color: i === 2 ? "#555" : "#222", wordBreak: "break-word" }}>{item.valor}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Abas */}
      <div style={{ display: "flex", borderBottom: "2px solid #e8e8e8", marginBottom: 20, overflowX: "auto" }}>
        {ABAS.map(aba => {
          const isAtiva = abaAtivaState === aba.key;
          return (
            <div key={aba.key} onClick={() => setAbaAtivaState(aba.key)}
              style={{ padding: isMobile ? "12px 14px" : "12px 28px", cursor: "pointer", fontSize: isMobile ? 12 : 13, fontWeight: isAtiva ? 700 : 500, color: isAtiva ? DARK_NAV : "#888", borderBottom: isAtiva ? `2px solid ${DARK_NAV}` : "2px solid transparent", marginBottom: -2, transition: "all 0.15s", letterSpacing: "0.5px", whiteSpace: "nowrap", flexShrink: 0 }}
              onMouseEnter={e => { if (!isAtiva) e.currentTarget.style.color = "#555"; }}
              onMouseLeave={e => { if (!isAtiva) e.currentTarget.style.color = "#888"; }}>
              {aba.label}
            </div>
          );
        })}
      </div>

      {abaAtivaState === "posicao"     && <TabPosicao    cartoes={cartoes} titulares={titulares} loading={loadingPos} />}
      {abaAtivaState === "utilizacao"  && <TabUtilizacao clienteId={cliente?.id} />}
      {abaAtivaState === "observacoes" && <TabObservacoes clienteId={cliente?.id} />}
    </div>
  );
}